import React from "react";
import { flagName, flagUrl, flagUrlSquare } from "../../shared/flags";

// Dev gallery: 5 ways to display "your flag" in the player row (the trigger). Inspired by
// OpenFront. Compares round vs square flags, framing and size. Route: /flagstyles
const SAMPLES = ["se", "us", "jp", "br", "in", "de"];
const CARET = <span style={{ color: "#8995b3", fontSize: 10, flex: "0 0 auto" }}>▾</span>;
const boxBase: React.CSSProperties = { display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.14)" };

const DESIGNS: { n: number; label: string; render: (code: string) => React.ReactNode }[] = [
  {
    n: 1, label: "Round (bigger, ringed)",
    render: (c) => (
      <button className="sx-plain" style={{ ...boxBase, height: 38, padding: "0 8px 0 4px", borderRadius: 19 }}>
        <img src={flagUrl(c)} width={32} height={32} alt="" style={{ display: "block", borderRadius: "50%", boxShadow: "0 0 0 1px rgba(255,255,255,0.3)" }} />
        {CARET}
      </button>
    ),
  },
  {
    n: 2, label: "Square card (OpenFront-style)",
    render: (c) => (
      <button className="sx-plain" style={{ ...boxBase, height: 38, padding: "0 8px 0 5px", borderRadius: 8 }}>
        <span style={{ width: 30, height: 30, borderRadius: 6, overflow: "hidden", border: "1px solid rgba(255,255,255,0.25)", display: "block", flex: "0 0 auto" }}>
          <img src={flagUrlSquare(c)} width={30} height={30} alt="" style={{ display: "block", objectFit: "cover" }} />
        </span>
        {CARET}
      </button>
    ),
  },
  {
    n: 3, label: "Rectangular flag (4:3)",
    render: (c) => (
      <button className="sx-plain" style={{ ...boxBase, height: 38, padding: "0 8px 0 5px", borderRadius: 8 }}>
        <span style={{ width: 42, height: 30, borderRadius: 4, overflow: "hidden", border: "1px solid rgba(255,255,255,0.2)", display: "block", flex: "0 0 auto", boxShadow: "0 1px 3px rgba(0,0,0,0.4)" }}>
          <img src={flagUrlSquare(c)} width={42} height={30} alt="" style={{ display: "block", width: "100%", height: "100%", objectFit: "cover" }} />
        </span>
        {CARET}
      </button>
    ),
  },
  {
    n: 4, label: "Round + code tag",
    render: (c) => (
      <button className="sx-plain" style={{ ...boxBase, height: 38, padding: "0 8px 0 4px", borderRadius: 19 }}>
        <img src={flagUrl(c)} width={30} height={30} alt="" style={{ display: "block", borderRadius: "50%" }} />
        <span style={{ fontFamily: "'Orbitron', monospace", fontWeight: 800, fontSize: 12, letterSpacing: 1, color: "#c3ccdf", background: "rgba(255,255,255,0.06)", borderRadius: 4, padding: "2px 5px" }}>{c.slice(0, 2).toUpperCase()}</span>
        {CARET}
      </button>
    ),
  },
  {
    n: 5, label: "Framed badge (square, shadow)",
    render: (c) => (
      <button className="sx-plain" style={{ display: "inline-flex", alignItems: "center", gap: 7, cursor: "pointer", background: "linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0.02))", border: "1px solid rgba(255,255,255,0.18)", height: 40, padding: "0 9px 0 5px", borderRadius: 9, boxShadow: "0 2px 8px rgba(0,0,0,0.35)" }}>
        <span style={{ width: 32, height: 32, borderRadius: 7, overflow: "hidden", border: "2px solid rgba(255,255,255,0.85)", display: "block", flex: "0 0 auto", boxShadow: "0 1px 4px rgba(0,0,0,0.5)" }}>
          <img src={flagUrlSquare(c)} width={32} height={32} alt="" style={{ display: "block", objectFit: "cover" }} />
        </span>
        {CARET}
      </button>
    ),
  },
];

export default function FlagStylesDemo() {
  return (
    <div style={{ maxWidth: 1000, margin: "0 auto", padding: "8px 16px 60px", fontFamily: "'Rajdhani', sans-serif", color: "#eaf0ff" }}>
      <h1 style={{ fontFamily: "'Orbitron', sans-serif", letterSpacing: 2, fontSize: 22 }}>Flag display designs</h1>
      <p style={{ color: "#8995b3", fontSize: 14, marginTop: 4 }}>
        How &ldquo;your flag&rdquo; shows in the player row (the button you click to change it). Each row is one
        design across a few countries so you can feel round vs square, size and framing.
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 16 }}>
        {DESIGNS.map((d) => (
          <div key={d.n} style={{ background: "#0b0f17", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, padding: "12px 14px" }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 10 }}>
              <span style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, color: "#37c8e0", fontSize: 15 }}>{d.n}</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: "#c3ccdf" }}>{d.label}</span>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
              {SAMPLES.map((c) => (
                <span key={c} title={flagName(c)} style={{ display: "inline-flex" }}>{d.render(c)}</span>
              ))}
            </div>
          </div>
        ))}
      </div>
      <p style={{ color: "#5f6a86", fontSize: 12.5, marginTop: 18 }}>Pick one and I&rsquo;ll wire it into the real player row.</p>
    </div>
  );
}
